import React, { useEffect, useState, useRef } from 'react';
import './Home.css';

const Home = () => {
  const [visibleSections, setVisibleSections] = useState({});
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({
              ...prev,
              [entry.target.dataset.section]: true,
            }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const sections = document.querySelectorAll('[data-section]');
    sections.forEach((section) => {
      if (observerRef.current) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const mainTools = [
    {
      title: 'AI Chat',
      description: 'Intelligent conversations with your documents for instant insights.',
      iconPath: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z',
      color: '#667eea',
    },
    {
      title: 'Smart Convert',
      description: 'Transform documents to any format with perfect quality.',
      iconPath: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
      color: '#f59e0b',
    },
    {
      title: 'Sign & Verify',
      description: 'Digital signatures with legal compliance and verification.',
      iconPath: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z',
      color: '#10b981',
    },
    {
      title: 'Edit & Merge',
      description: 'Professional editing and seamless document combination.',
      iconPath: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
      color: '#8b5cf6',
    },
  ];

  const features = [
    {
      iconPath: 'M13 10V3L4 14h7v7l9-11h-7z',
      title: 'Instant Processing',
      description: 'Upload and process documents in seconds with our lightning-fast AI engine.',
    },
    {
      iconPath: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
      title: 'Military-Grade Security',
      description: 'End-to-end encryption, GDPR compliant, and SOC2 certified protection.',
    },
    {
      iconPath: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      title: 'Multi-Language',
      description: 'Support for 100+ languages with accurate translation and OCR.',
    },
    {
      iconPath: 'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z',
      title: 'Cross-Platform',
      description: 'Works seamlessly on desktop, tablet, and mobile devices.',
    },
    {
      iconPath: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
      title: 'Unlimited Processing',
      description: 'No file size limits or processing caps with Pro plans.',
    },
    {
      iconPath: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
      title: 'Batch Operations',
      description: 'Process hundreds of documents simultaneously with bulk tools.',
    },
  ];

  const howItWorks = [
    {
      step: '1',
      title: 'Upload Your Document',
      description: 'Drag and drop or click to upload any document format - PDF, Word, Excel, PowerPoint, and more.',
      iconPath: 'M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12',
    },
    {
      step: '2',
      title: 'Choose Your Tool',
      description: 'Select from 50+ AI-powered tools - convert, edit, sign, merge, compress, or analyze your documents.',
      iconPath: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
    },
    {
      step: '3',
      title: 'Download & Share',
      description: 'Get your processed document instantly. Download or share directly via email, cloud storage, or link.',
      iconPath: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4',
    },
  ];

  const securityFeatures = [
    {
      title: 'End-to-End Encryption',
      description: 'All documents are encrypted during upload, processing, and storage using AES-256 encryption.',
      iconPath: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
    },
    {
      title: 'Auto-Delete',
      description: 'Files are automatically deleted from our servers within 1 hour after processing.',
      iconPath: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    },
    {
      title: 'GDPR Compliant',
      description: 'Full compliance with EU data protection regulations and international privacy standards.',
      iconPath: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    },
    {
      title: 'SOC2 Certified',
      description: 'Independently audited and certified for security, availability, and confidentiality.',
      iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    },
  ];

  const useCases = [
    {
      iconPath: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
      title: 'Business Professionals',
      description: 'Streamline contracts, proposals, and reports with AI-powered workflows.',
      link: '/lp/xbt1l',
    },
    {
      iconPath: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
      title: 'Students & Educators',
      description: 'Organize notes, research papers, and study materials effortlessly.',
      link: '/lp/xbt1l',
    },
    {
      iconPath: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3',
      title: 'Legal Teams',
      description: 'Secure document signing, redaction, and compliance management.',
      link: '/lp/xbt1l',
    },
  ];

  const stats = [
    { number: '2.5K+', label: 'Happy Customers', iconPath: 'M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5' },
    { number: '150K+', label: 'Documents Processed', iconPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { number: '99.9%', label: 'Uptime SLA', iconPath: 'M13 10V3L4 14h7v7l9-11h-7z' },
    { number: '50+', label: 'AI-Powered Tools', iconPath: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' },
  ];

  const testimonials = [
    {
      name: 'Lars Andersson',
      role: 'Operations Manager',
      company: 'TechFlow Inc',
      text: 'Incredible tool! The AI features cut our document processing time by 75%. Game changer for our team.',
      rating: 5,
    },
    {
      name: 'Sophie Dubois',
      role: 'Legal Advisor',
      company: 'Smith & Partners',
      text: 'Security and compliance features are top-notch. Perfect for handling sensitive legal documents.',
      rating: 5,
    },
    {
      name: 'Marco Bianchi',
      role: 'Project Coordinator',
      company: 'Global Solutions',
      text: 'Best investment we made this year. The batch processing saves us countless hours every week.',
      rating: 5,
    },
  ];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero" data-section="hero">
        <div className="hero-container">
          <div className={`hero-content ${visibleSections.hero ? 'animate-in' : ''}`}>
            <div className="hero-tag">
              <span className="tag-icon">✨</span>
              <span className="tag-text">AI-Powered Document Management</span>
            </div>
            <h1 className="hero-title">
              Transform Your Documents
              <span className="gradient-text"> with AI Intelligence</span>
            </h1>
            <p className="hero-description">
              Experience the future of document management with DocuAIHub. 
              Convert, edit, sign, and analyze your documents with powerful AI tools. 
              Save hours every week with automated workflows.
            </p>
            <div className="hero-actions">
              <a href="/lp/xbt1l" className="btn-hero-primary">
                <span>Subscribe Now</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </a>
            </div>
            <div className="hero-stats">
              {stats.map((stat, index) => (
                <React.Fragment key={index}>
                  <div className="stat-item">
                    <div className="stat-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d={stat.iconPath} strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="stat-number">{stat.number}</div>
                    <div className="stat-label">{stat.label}</div>
                  </div>
                  {index < stats.length - 1 && <div className="stat-divider"></div>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
        <div className="hero-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
        </div>
      </section>

      {/* Main Tools Section */}
      <section id="features" className="main-tools" data-section="tools">
        <div className="section-header">
          <h2 className={visibleSections.tools ? 'animate-in' : ''}>
            Powerful Tools for Every Document Task
          </h2>
          <p className={visibleSections.tools ? 'animate-in' : ''}>
            Everything you need in one intelligent platform
          </p>
        </div>
        <div className="tools-grid">
          {mainTools.map((tool, index) => (
            <div
              key={index}
              className={`tool-card ${visibleSections.tools ? 'animate-in' : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="tool-icon" style={{ backgroundColor: `${tool.color}15`, color: tool.color }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d={tool.iconPath} strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>{tool.title}</h3>
              <p>{tool.description}</p>
              <a href="/lp/xbt1l" className="tool-link">
                Try Now →
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* Features Grid Section */}
      <section className="features-section" data-section="features">
        <div className="section-header">
          <h2 className={visibleSections.features ? 'animate-in' : ''}>
            Built for Performance & Security
          </h2>
          <p className={visibleSections.features ? 'animate-in' : ''}>
            Enterprise-grade features trusted by thousands
          </p>
        </div>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`feature-card ${visibleSections.features ? 'animate-in' : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d={feature.iconPath} strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works" data-section="howitworks">
        <div className="section-header">
          <h2 className={visibleSections.howitworks ? 'animate-in' : ''}>
            How It Works
          </h2>
          <p className={visibleSections.howitworks ? 'animate-in' : ''}>
            Get started in three simple steps
          </p>
        </div>
        <div className="steps-container">
          {howItWorks.map((step, index) => (
            <div
              key={index}
              className={`step-card ${visibleSections.howitworks ? 'animate-in' : ''}`}
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="step-number">{step.step}</div>
              <div className="step-icon">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d={step.iconPath} strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Security Section */}
      <section className="security-section" data-section="security">
        <div className="section-header">
          <h2 className={visibleSections.security ? 'animate-in' : ''}>
            Enterprise-Grade Security
          </h2>
          <p className={visibleSections.security ? 'animate-in' : ''}>
            Your data is protected at every step
          </p>
        </div>
        <div className="security-grid">
          {securityFeatures.map((feature, index) => (
            <div
              key={index}
              className={`security-card ${visibleSections.security ? 'animate-in' : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="security-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d={feature.iconPath} strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="use-cases" data-section="usecases">
        <div className="section-header">
          <h2 className={visibleSections.usecases ? 'animate-in' : ''}>
            Perfect for Every Professional
          </h2>
          <p className={visibleSections.usecases ? 'animate-in' : ''}>
            Tailored solutions for your industry
          </p>
        </div>
        <div className="use-cases-grid">
          {useCases.map((useCase, index) => (
            <a
              href={useCase.link}
              key={index}
              className={`use-case-card ${visibleSections.usecases ? 'animate-in' : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="use-case-icon">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d={useCase.iconPath} strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>{useCase.title}</h3>
              <p>{useCase.description}</p>
              <span className="use-case-arrow">Learn More →</span>
            </a>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials" data-section="testimonials">
        <div className="section-header">
          <h2 className={visibleSections.testimonials ? 'animate-in' : ''}>
            Trusted by Professionals Worldwide
          </h2>
          <p className={visibleSections.testimonials ? 'animate-in' : ''}>
            Real results from real customers
          </p>
        </div>
        <div className="testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className={`testimonial-card ${visibleSections.testimonials ? 'animate-in' : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="testimonial-header">
                <div className="testimonial-avatar">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="rating">
                  {'⭐'.repeat(testimonial.rating)}
                </div>
              </div>
              <p className="testimonial-text">"{testimonial.text}"</p>
              <div className="testimonial-author">
                <div className="author-name">{testimonial.name}</div>
                <div className="author-role">{testimonial.role} • {testimonial.company}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
